import { Tag, Tooltip } from 'antd';
import { InspectionItemStatus, InspectionItemStatusLabels, InspectionItemStatusColors } from '@/shared/enums/InspectionStatus';

interface StatusBadgeProps {
  /**
   * Modo 1: Status específico de inspeção (usa enum e labels automáticos)
   */
  status?: InspectionItemStatus | number;
  /**
   * Modo 2: Uso genérico com label e color customizados
   */
  label?: string;
  color?: string;
  /**
   * Exibir tooltip com descrição do status (apenas no modo status)
   */
  showTooltip?: boolean;
  /**
   * Tamanho do badge
   */
  size?: 'small' | 'default';
  /**
   * Estilo customizado
   */
  style?: React.CSSProperties;
}

/**
 * Componente consolidado de StatusBadge que suporta:
 * - Uso específico: status de inspeção (usa enum e labels automáticos)
 * - Uso genérico: label e color customizados
 */
export function StatusBadge({
  status,
  label,
  color,
  showTooltip = false,
  size = 'default',
  style,
}: StatusBadgeProps) {
  // Modo 1: Uso com status enum
  if (status !== undefined) {
    const statusValue = typeof status === 'number' ? status as InspectionItemStatus : status;
    const statusLabel = InspectionItemStatusLabels[statusValue] || 'Status Desconhecido';
    const statusColor = InspectionItemStatusColors[statusValue] || '#d9d9d9';

    const statusBadge = (
      <Tag
        color={statusColor}
        style={{
          fontSize: size === 'small' ? '12px' : '14px',
          fontWeight: 500,
          margin: 0,
          ...style,
        }}
      >
        {statusLabel}
      </Tag>
    );

    if (showTooltip) {
      return (
        <Tooltip title={getStatusDescription(statusValue)}>
          {statusBadge}
        </Tooltip>
      );
    }

    return statusBadge;
  }

  // Modo 2: Uso genérico com label e color
  if (label && color) {
    return (
      <Tag
        color={color}
        style={{
          fontSize: size === 'small' ? '12px' : '14px',
          fontWeight: 500,
          margin: 0,
          ...style,
        }}
      >
        {label}
      </Tag>
    );
  }

  // Fallback: se nenhum modo foi especificado corretamente
  return null;
}

/**
 * Retorna a descrição detalhada de um status de inspeção
 */
function getStatusDescription(status: InspectionItemStatus): string {
  const descriptions: Partial<Record<InspectionItemStatus, string>> = {
    [InspectionItemStatus.AguardandoDirecionamento]: "A inspeção foi solicitada e aguarda direcionamento para uma prestadora.",
    [InspectionItemStatus.AguardandoAceitePrestadora]: "A inspeção foi direcionada para uma prestadora e aguarda aceite.",
    [InspectionItemStatus.PrestadoraRecusou]: "A prestadora recusou a inspeção. O banco pode direcionar para outra prestadora.",
    [InspectionItemStatus.AguardandoDirecionamentoInspetor]: "A prestadora aceitou a inspeção e deve direcionar para um inspetor.",
    [InspectionItemStatus.AguardandoAceiteInspetor]: "A inspeção foi direcionada para um inspetor e aguarda aceite.",
    [InspectionItemStatus.InspetorRecusou]: "O inspetor recusou a inspeção. A prestadora pode direcionar para outro inspetor.",
    [InspectionItemStatus.AguardandoAgendamento]: "O inspetor aceitou a inspeção e deve fazer o agendamento.",
    [InspectionItemStatus.Agendado]: "A inspeção foi agendada e aguarda execução.",
    [InspectionItemStatus.CheckinRealizado]: "O inspetor fez checkin no local e está preenchendo o checklist.",
    [InspectionItemStatus.Finalizado]: "A inspeção foi finalizada com sucesso.",
    [InspectionItemStatus.Cancelado]: "A inspeção foi cancelada.",
    [InspectionItemStatus.Frustrado]: "A inspeção foi frustrada por algum motivo.",
    [InspectionItemStatus.AguardandoGeracaoLaudo]: "Aguardando geração do laudo após finalização da inspeção.",
    [InspectionItemStatus.AguardandoRevisaoPrestadora]: "Laudo gerado, aguardando revisão pela prestadora.",
    [InspectionItemStatus.LaudoNegadoPorPrestadora]: "O laudo foi negado pela prestadora e precisa de correções.",
    [InspectionItemStatus.AguardandoRevisaoBanco]: "Laudo aprovado pela prestadora, aguardando revisão pelo banco.",
    [InspectionItemStatus.LaudoNegadoPorBanco]: "O laudo foi negado pelo banco e precisa de correções.",
    [InspectionItemStatus.CorrecaoPeloInspetorBanco]: "O inspetor do banco está corrigindo o laudo.",
    [InspectionItemStatus.LaudoAceitoPeloBanco]: "O laudo foi aceito pelo banco e está finalizado.",
    [InspectionItemStatus.TelevistoriaAgendada]: "Televistoria (videoconferência) foi agendada.",
    [InspectionItemStatus.TelevistoriaFinalizada]: "Televistoria finalizada, aguardando geração do laudo.",
    [InspectionItemStatus.LaudoDevolvido]: "O laudo foi devolvido para correção.",
    [InspectionItemStatus.DevolvidoPeloBancoAposAceite]: "O banco devolveu o laudo após aceite para correções pela prestadora.",
    [InspectionItemStatus.AguardandoPreenchimentoPrestadora]: "Aguardando preenchimento pela prestadora (sem inspetor).",
    [InspectionItemStatus.ProcessandoScraping]: "Buscando imóveis comparáveis na região para análise automática.",
    [InspectionItemStatus.AguardandoAnaliseIA]: "Scraping concluído, aguardando análise pela inteligência artificial.",
    [InspectionItemStatus.ProcessandoAnaliseIA]: "A IA está analisando e selecionando as melhores amostras.",
    [InspectionItemStatus.LaudoAutomaticoGerado]: "O laudo automático foi gerado com sucesso pela IA.",
    [InspectionItemStatus.ScrapingFalhou]: "Falha ao buscar imóveis comparáveis. Tente novamente.",
    [InspectionItemStatus.AnaliseIAFalhou]: "Falha na análise da IA. Tente novamente.",
  };

  return descriptions[status] || "Status não reconhecido.";
}



